import { useCallback } from 'react'
import ProcessEmissionsAssetEmissionCalculateButton from './ProcessEmissionsAssetEmissionCalculateButton'
import { TableCell, TableRow, TableRowHeader } from '@/common'
import { UnitLabel } from '@/shared/config/config'
import useI18n from '@/shared/hooks/useI18n'
import { EmissionTypeToReport } from './ProcessEmissionsSection'
import { EmissionEditState } from './ProcessEmissionsReporting'

export default function CombustionProcessEmissionsTableRow({
  assetWithMetadata,
  setIsAddingEmission,
  isViewMode,
  onEmissionRemove
}: {
  assetWithMetadata: EmissionTypeToReport
  setIsAddingEmission: (emission: EmissionEditState | null) => void
  isViewMode: boolean
  onEmissionRemove: (emissionRecordId: string) => void
}) {
  const { t, numberFormatter2Decimals } = useI18n()
  const {
    emissionType,
    possibleMethods,
    productionRouteAsset: { id, assetDefinitionId, isCustom, customName, customId, usesFuel },
    emissionRecord
  } = assetWithMetadata

  const onCalculateClick = useCallback(
    (emissionType: string, method: string) => {
      setIsAddingEmission({
        productionRouteAssetId: id,
        emissionType: emissionType,
        method
      })
    },
    [id, setIsAddingEmission]
  )

  const onRemoveClick = useCallback(() => {
    onEmissionRemove(emissionRecord?.id!)
  }, [emissionRecord?.id, onEmissionRemove])

  const displayName = isCustom ? customName : t(`assets:${assetDefinitionId}`)
  return (
    <tbody>
      <TableRow>
        <TableRowHeader className="text-left">{displayName} </TableRowHeader>
        <TableCell className="text-sm">{customId ?? '-'}</TableCell>
        <TableCell className="text-sm">
          {usesFuel && emissionRecord?.artifacts.fuelType
            ? t(`common:fuelTypes.${emissionRecord?.artifacts.fuelType}`)
            : '-'}
        </TableCell>
        <TableCell className="text-right">
          {emissionRecord?.artifacts.emissionFactor
            ? `${numberFormatter2Decimals(emissionRecord.artifacts?.emissionFactor)} ${UnitLabel.tCO2PerT}`
            : '-'}
        </TableCell>
        <TableCell className="text-right">
          {emissionRecord?.artifacts?.fuelConsumed
            ? `${numberFormatter2Decimals(emissionRecord.artifacts?.fuelConsumed)} ${UnitLabel.t}`
            : '-'}
        </TableCell>
        <TableCell className="text-right">
          {emissionRecord?.emission
            ? `${numberFormatter2Decimals(emissionRecord?.emission)} ${UnitLabel.tCO2}`
            : '-'}
        </TableCell>
        <TableCell className="text-right">
          {(emissionRecord ? [emissionRecord.method] : possibleMethods).map(method => (
            <ProcessEmissionsAssetEmissionCalculateButton
              key={method}
              emissionType={emissionType}
              onCalculateClick={onCalculateClick}
              isViewMode={isViewMode}
              method={method}
              hasEmissionRecord={!!emissionRecord}
              onRemove={onRemoveClick}
            />
          ))}
        </TableCell>
      </TableRow>
    </tbody>
  )
}
